// InvoiceForm.jsx
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { X, Plus, Trash2, FileText, Save, CheckCircle2, Search } from "lucide-react";
import { BASE_URL } from "shared/lib/config";
import { toast } from "react-toastify";

export const DEFAULT_FROM = {
  companyName: "Deus Human Capital Services",
  companyEmail: "admin@deushcs.com",
  companyAddress:
    "My Republic Plaza, BSD Green Office Park 6, Wing A Lantai Dasar Zona 6 Jl. BSD Green Office Park Kel. Sampora Kec. Cisauk Kab. Tangerang - Banten 15345",
};

/* =========================================================
 * Auth + fetch helpers (same spirit as CreateSalesForm.jsx)
 * ========================================================= */
const getStore = () => {
  if (sessionStorage.getItem("idToken")) return sessionStorage;
  if (localStorage.getItem("idToken")) return localStorage;
  return localStorage;
};
const getToken = () => getStore().getItem("idToken");

async function fetchJson(url, opts = {}) {
  const token = getToken();
  const headers = {
    "Content-Type": "application/json",
    ...(opts.headers || {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const res = await fetch(url, { ...opts, headers });
  const contentType = res.headers.get("content-type") || "";

  let data = null;
  if (contentType.includes("application/json")) data = await res.json().catch(() => null);
  else data = await res.text().catch(() => null);

  if (!res.ok) {
    const msg = data?.message || (typeof data === "string" && data) || `Request failed (${res.status})`;
    const err = new Error(msg);
    err.status = res.status;
    err.data = data;
    throw err;
  }

  return data;
}

/* =========================================================
 * Helpers
 * ========================================================= */
const toDateInput = (v) => {
  if (!v) return "";
  try {
    return new Date(v).toISOString().slice(0, 10);
  } catch {
    return "";
  }
};

const autoGrowTextarea = (el) => {
  if (!el) return;
  el.style.height = "auto";
  el.style.height = `${el.scrollHeight}px`;
};

const formatCurrency = (amount) =>
  new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
  }).format(Number(amount) || 0);

const generateLineId = () => `line_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

const clampPercent = (n) => {
  const x = Number(n);
  if (!Number.isFinite(x)) return 0;
  return Math.max(0, Math.min(100, x));
};

export default function InvoiceForm({ onClose, onSubmit, initialData }) {
  const BANK_SUGGESTIONS = useMemo(
    () => [
      "Bank Central Asia (BCA)",
      "Bank Mandiri",
      "Bank Rakyat Indonesia (BRI)",
      "Bank Negara Indonesia (BNI)",
      "CIMB Niaga",
      "Bank Danamon",
      "Bank Permata",
      "Bank Syariah Indonesia (BSI)",
      "OCBC NISP",
      "Maybank Indonesia",
      "Panin Bank",
      "Bank BTN",
      "Bank Mega",
      "HSBC Indonesia",
      "Standard Chartered Bank",
      "Citibank N.A.",
      "DBS Bank",
      "UOB Indonesia",
    ],
    []
  );

  const buildInitialFormData = useCallback(
    (data) => ({
      // dates
      invoiceDate: toDateInput(data?.invoiceDate) || new Date().toISOString().slice(0, 10),
      dueDate: toDateInput(data?.dueDate) || "",

      // items table (match Sales form style: qty x price, amount computed)
      lines: Array.isArray(data?.lines)
        ? data.lines.map((l, idx) => ({
            ...l,
            id: l.id || `line_${idx}_${Date.now()}`,
            productName: l.productName ?? l.name ?? l.product ?? "",
            description: l.description ?? "",
            qty: Number(l.qty) || 1,
            unitPrice: Number(l.unitPrice) || 0,
            lineTotal: Number(l.lineTotal) || 0,
          }))
        : Array.isArray(data?.items)
        ? data.items.map((it, idx) => ({
            id: `line_${idx}_${Date.now()}`,
            productName: it.name || "",
            description: it.description || "",
            qty: Math.max(1, Number(it.qty) || 1),
            unitPrice: Math.max(0, Number(it.price) || 0),
            lineTotal: Math.max(0, Number(it.amount) || 0),
          }))
        : [{ id: generateLineId(), productName: "", description: "", qty: 1, unitPrice: 0, lineTotal: 0 }],

      // backend-required blocks
      fromCompanyName: data?.from?.companyName || DEFAULT_FROM.companyName,
      fromCompanyEmail: data?.from?.companyEmail || DEFAULT_FROM.companyEmail,
      fromCompanyAddress: data?.from?.companyAddress || DEFAULT_FROM.companyAddress,
      selectedClientId: (() => {
  const v = data?.client_id ?? data?.clientId ?? "";
  if (typeof v === "string") return v;
  if (v && typeof v === "object") return String(v._id || v.id || "");
  return "";
})(),

      billToCompanyName: data?.billTo?.companyName || "",
      billToPicName: data?.billTo?.picName || "",
      billToPicTitle: data?.billTo?.picTitle || "",
      billToCompanyAddress: data?.billTo?.companyAddress || "",
      billToEmailOrPhone: data?.billTo?.emailOrPhone || "",
      billToReference: data?.billTo?.reference || "",

              paymentBank:
          data?.payment?.bank ??
          "Bank Central Asia (BCA)",

        paymentAccountName:
          data?.payment?.accountName ??
          "PT Investama Persada Kurnia",

        paymentAccountNo:
          data?.payment?.accountNo ??
          "7305051331",

      paymentNotes: data?.payment?.notes || "",

      // PPN now global % (like Sales form). If editing existing invoice, keep a best-effort guess:
     // schema: rates are stored as percentages 0..100
      ppnPercent: typeof data?.rates?.ppn === "number" ? clampPercent(data.rates.ppn) : 11,
      pphPercent: typeof data?.rates?.pph === "number" ? clampPercent(data.rates.pph) : 0,
      discountPercent: typeof data?.rates?.discount === "number" ? clampPercent(data.rates.discount) : 0,


      // signer
      signatureDate: toDateInput(data?.meta?.signatureDate || data?.signatureDate) || "",
      signatureName: data?.signatureName || "Gregory Kurniawan",
      signatureTitle: data?.signatureTitle || "CEO",
    }),
    []
  );
const validateDatePair = (invoiceDate, dueDate) => {
  if (!invoiceDate || !dueDate) return ""; // allow empty (draft)
  const inv = new Date(invoiceDate);
  const due = new Date(dueDate);
  if (Number.isNaN(inv.getTime()) || Number.isNaN(due.getTime())) return "";

  if (due < inv) {
    return "Tanggal jatuh tempo harus sama atau setelah tanggal invoice.";
  }
  return "";
};

  const [formData, setFormData] = useState(() => buildInitialFormData(initialData));
  const [submitting, setSubmitting] = useState(false);
  const [dateError, setDateError] = useState("");

  const isEditMode = useMemo(() => !!(initialData?._id || initialData?.id), [initialData]);
  const currentStatus = useMemo(() => {
  // normalize because some data might use statusLabel etc.
  return String(initialData?.status || "").trim().toLowerCase();
}, [initialData]);


const canIssue = !isEditMode || currentStatus === "draft";

const hasSalesId = useMemo(() => {
  // 1) explicit sales_id / salesId / populated sales
  const v = initialData?.sales_id ?? initialData?.salesId ?? initialData?.sales;
  const direct =
    (typeof v === "string" && v.trim()) ||
    (typeof v === "object" && (v?._id || v?.id));

  if (direct) return true;

  // 2) business sales id stored in BillTo.reference (your real “Sales ID”)
  const ref = initialData?.billTo?.reference;
  return typeof ref === "string" && ref.trim().length > 0;
}, [initialData]);

const lockSalesLinkedFields = isEditMode && hasSalesId;
// show reference ONLY when editing AND invoice is sales-linked
const showReferenceField = isEditMode && hasSalesId;

useEffect(() => {
  setDateError(validateDatePair(formData.invoiceDate, formData.dueDate));
}, [formData.invoiceDate, formData.dueDate]);


  useEffect(() => {
    setFormData(buildInitialFormData(initialData));
  }, [initialData, buildInitialFormData]);

  /* =========================================================
   * Bill-To Client Suggestions (keyboard navigable)
   * - fetch clients
   * - type in "Nama Perusahaan" -> shows dropdown
   * - Enter/Click -> autofill billTo fields
   * ========================================================= */
  const [loadingClients, setLoadingClients] = useState(false);
  const [clients, setClients] = useState([]);

  useEffect(() => {
    // fetch once when form mounts
    const load = async () => {
      setLoadingClients(true);
      try {
        const data = await fetchJson(`${BASE_URL}/api/invoice-tracker/clients?limit=500`);
        setClients(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error(err);
        setClients([]);
      } finally {
        setLoadingClients(false);
      }
    };
    load();
  }, []);
  useEffect(() => {
  if (!clients?.length) return;
  if (formData.selectedClientId) return;

  const ref = String(formData.billToReference || "").trim().toLowerCase();
  const name = String(formData.billToCompanyName || "").trim().toLowerCase();

  if (!ref && !name) return;

  const found = clients.find((c) => {
    const cid = String(c?.client_id || "").trim().toLowerCase();
    const cname = String(c?.client_name || c?.companyName || c?.name || "").trim().toLowerCase();
    return (ref && cid && cid === ref) || (name && cname === name);
  });

  if (found?._id) {
    const b = normalizeClientToBillTo(found);
    setFormData((prev) => ({
      ...prev,
      selectedClientId: String(found._id),
      // optionally also normalize the visible fields to be consistent:
      billToCompanyName: b.companyName || prev.billToCompanyName,
      billToPicName: b.picName || prev.billToPicName,
      billToPicTitle: b.picTitle || prev.billToPicTitle,
      billToCompanyAddress: b.companyAddress || prev.billToCompanyAddress,
      billToEmailOrPhone: b.emailOrPhone || prev.billToEmailOrPhone,
      // if you want: prefer client_id as reference when empty
      billToReference: prev.billToReference || "",
    }));
  }
}, [clients, formData.selectedClientId, formData.billToReference, formData.billToCompanyName]);

  const billToInputWrapRef = useRef(null);
  const billToInputRef = useRef(null);
  const [billToSuggestOpen, setBillToSuggestOpen] = useState(false);
  const [billToActiveIdx, setBillToActiveIdx] = useState(-1);

  // normalize a client from backend to the billTo shape you want to fill
  const normalizeClientToBillTo = (c) => {
    // These keys may differ in your backend; this mapping is defensive.
    const companyName = c?.client_name || c?.companyName || c?.name || "";
    const picName = c?.pic_name || c?.picName || c?.pic || c?.contact_name || "";
    const picTitle = c?.pic_title || c?.picTitle || c?.title || "";
    const companyAddress = c?.client_address || c?.address || c?.companyAddress || "";
    const emailOrPhone =
      c?.email_or_phone || c?.emailOrPhone || c?.email || c?.phone || c?.contact || "";

    // optional reference (nice to include client_id)
    const reference =
      c?.client_id ? `${c.client_id}` : (c?.reference || "");

    return { companyName, picName, picTitle, companyAddress, emailOrPhone, reference };
  };

  const filteredBillToSuggestions = useMemo(() => {
    const q = String(formData.billToCompanyName || "").trim().toLowerCase();
    if (!q) return [];

    // match by company name or client id
    const matches = (clients || []).filter((c) => {
      const name = String(c?.client_name || c?.companyName || c?.name || "").toLowerCase();
      const id = String(c?.client_id || "").toLowerCase();
      return name.includes(q) || (id && id.includes(q));
    });

    // cap for UI
    return matches.slice(0, 8);
  }, [clients, formData.billToCompanyName]);

  const selectBillToSuggestion = (clientObj) => {
    const b = normalizeClientToBillTo(clientObj);

    setFormData((prev) => ({
      ...prev,
      selectedClientId: String(clientObj?._id || ""), 
      billToCompanyName: b.companyName || prev.billToCompanyName,
      billToPicName: b.picName || "",
      billToPicTitle: b.picTitle || "",
      billToCompanyAddress: b.companyAddress || "",
      billToEmailOrPhone: b.emailOrPhone || "",
      // keep existing reference if user typed it manually; otherwise use client_id
      billToReference: prev.billToReference || "",

    }));

    setBillToSuggestOpen(false);
    setBillToActiveIdx(-1);

    // keep focus on input
    requestAnimationFrame(() => {
      billToInputRef.current?.focus();
    });
  };

  // close suggestions when clicking outside
  useEffect(() => {
    const onDocDown = (e) => {
      if (!billToInputWrapRef.current) return;
      if (billToInputWrapRef.current.contains(e.target)) return;
      setBillToSuggestOpen(false);
      setBillToActiveIdx(-1);
    };
    document.addEventListener("mousedown", onDocDown);
    return () => document.removeEventListener("mousedown", onDocDown);
  }, []);

  /* =========================================================
   * Lines: match Sales Items style (no discount/tax columns)
   * ========================================================= */
  const computeLine = (line) => {
    const qty = Math.max(1, Number(line.qty) || 1);
    const unitPrice = Math.max(0, Number(line.unitPrice) || 0);
    const amount = Math.max(0, qty * unitPrice);
    return { qty, unitPrice, amount };
  };

 const addLine = useCallback(() => {
  if (lockSalesLinkedFields) return;
  setFormData((prev) => ({
    ...prev,
    lines: [
      ...(prev.lines || []),
      { id: generateLineId(), productName: "", description: "", qty: 1, unitPrice: 0, lineTotal: 0 },
    ],
  }));
}, [lockSalesLinkedFields]);


 const removeLine = (id) => {
  if (lockSalesLinkedFields) return;
  setFormData((prev) => {
    const next = (prev.lines || []).filter((l) => l.id !== id);
    return { ...prev, lines: next.length ? next : prev.lines };
  });
};


  const updateLine = (id, field, value) => {
  if (lockSalesLinkedFields) return;
  setFormData((prev) => ({
    ...prev,
    lines: (prev.lines || []).map((line) => {
      if (line.id !== id) return line;
      const updated = { ...line, [field]: value };
      const { amount } = computeLine(updated);
      updated.lineTotal = amount;
      return updated;
    }),
  }));
};


const calculateSubtotal = () =>
  (formData.lines || []).reduce((sum, line) => sum + (Number(computeLine(line).amount) || 0), 0);

const calculateDiscountAmount = () => {
  const subtotal = calculateSubtotal();
  const discPct = clampPercent(formData.discountPercent);
  return Math.max(0, (subtotal * discPct) / 100);
};

const calculateAfterDiscount = () => {
  const subtotal = calculateSubtotal();
  const disc = calculateDiscountAmount();
  return Math.max(0, subtotal - disc);
};

const calculatePpnAmount = () => {
  const base = calculateAfterDiscount(); // PPN applied after discount
  const ppnPct = clampPercent(formData.ppnPercent);
  return Math.max(0, (base * ppnPct) / 100);
};

const calculatePphAmount = () => {
  const base = calculateAfterDiscount(); // PPh calculated from net base (after discount)
  const pphPct = clampPercent(formData.pphPercent);
  return Math.max(0, (base * pphPct) / 100);
};

const calculateGrandTotal = () => {
  const base = calculateAfterDiscount();
  const ppn = calculatePpnAmount();
  const pph = calculatePphAmount();
  return Math.max(0, base + ppn - pph);
};


  /* =========================================================
   * API Payload
   * ========================================================= */
  const buildApiPayload = async ({ status }) => {
    const subtotalGross = calculateSubtotal();
    const discountAmount = calculateDiscountAmount();
    const subtotalNet = calculateAfterDiscount();

    const ppnAmount = calculatePpnAmount();
    const pphAmount = calculatePphAmount();

    const grandTotal = calculateGrandTotal();


    const items = (formData.lines || [])
  .filter((l) => (Number(l.qty) || 0) >= 1)
  .map((l) => {
    const { qty, unitPrice, amount } = computeLine(l);

    const name = String(l.productName || "").trim();
    const rawDesc = String(l.description || "").trim();

    const description =
      rawDesc || (name ? `${name} - Term 1` : "");

    return {
      name,
      description,
      qty,
      price: unitPrice,
      amount,
    };
  });


   const payload = {
  client_id: formData.selectedClientId,

  invoiceDate: formData.invoiceDate ? new Date(formData.invoiceDate).toISOString() : undefined,
  dueDate: formData.dueDate ? new Date(formData.dueDate).toISOString() : undefined,

  from: {
    companyName: formData.fromCompanyName,
    companyEmail: formData.fromCompanyEmail,
    companyAddress: formData.fromCompanyAddress,
  },

  billTo: {
    companyName: formData.billToCompanyName,
    picName: formData.billToPicName,
    picTitle: formData.billToPicTitle || "",
    companyAddress: formData.billToCompanyAddress,
    emailOrPhone: formData.billToEmailOrPhone || "",
    reference: formData.billToReference || "",
  },

  items,

  rates: {
    ppn: clampPercent(formData.ppnPercent),
    pph: clampPercent(formData.pphPercent),
    discount: clampPercent(formData.discountPercent),
  },

  amounts: {
    subtotal_gross: Math.max(0, Number(subtotalGross) || 0),
    discount: Math.max(0, Number(discountAmount) || 0),
    subtotal_net: Math.max(0, Number(subtotalNet) || 0),
    ppn: Math.max(0, Number(ppnAmount) || 0),
    pph: Math.max(0, Number(pphAmount) || 0),
  },

  totals: {
    subtotal: Math.max(0, Number(subtotalNet) || 0),
    ppn: Math.max(0, Number(ppnAmount) || 0),
    pph: Math.max(0, Number(pphAmount) || 0),
    grandTotal: Math.max(0, Number(grandTotal) || 0),
  },

  payment: {
    bank: formData.paymentBank,
    accountName: formData.paymentAccountName,
    accountNo: formData.paymentAccountNo,
    notes: formData.paymentNotes || "",
  },

  signatureName: formData.signatureName || "",
  signatureTitle: formData.signatureTitle || "",

  meta: {
    signatureDate: formData.signatureDate ? new Date(formData.signatureDate).toISOString() : undefined,
  },
};


    if (typeof status !== "undefined") payload.status = status;
    return payload;
  };

  /* =========================================================
   * Draft/Issued Validation
   * ========================================================= */
  const hasAnyMeaningfulDraftContent = () => {
    const t = (v) => String(v || "").trim();

    const anyHeader =
      t(formData.fromCompanyName) ||
      t(formData.fromCompanyEmail) ||
      t(formData.fromCompanyAddress) ||
      t(formData.billToCompanyName) ||
      t(formData.billToPicName) ||
      t(formData.billToCompanyAddress) ||
      t(formData.billToEmailOrPhone) ||
      t(formData.billToReference) ||
      t(formData.paymentBank) ||
      t(formData.paymentAccountName) ||
      t(formData.paymentAccountNo) ||
      t(formData.paymentNotes) ||
      t(formData.signatureName) ||
      t(formData.signatureTitle);

    const anyLines = (formData.lines || []).some((l) => {
      const prod = t(l.productName);
      const desc = t(l.description);
      const qty = Number(l.qty) || 0;
      const price = Number(l.unitPrice) || 0;
      return prod || desc || qty > 0 || price > 0;
    });

    const anyDate = t(formData.invoiceDate) || t(formData.dueDate);

    return Boolean(anyHeader || anyLines || anyDate);
  };

  const validateForIssued = () => {
    const missing = [];
    if (!formData.invoiceDate) missing.push("Tgl. Invoice");
    if (!formData.dueDate) missing.push("Tgl. Jatuh Tempo");
    if (!formData.signatureDate) missing.push("Tanggal Penandatangan");
    if (!formData.signatureName) missing.push("Nama Penandatangan");
    if (!formData.signatureTitle) missing.push("Jabatan Penandatangan");

    if (!formData.fromCompanyName) missing.push("Dari: Nama Perusahaan");
    if (!formData.fromCompanyEmail) missing.push("Dari: Email Perusahaan");
    if (!formData.fromCompanyAddress) missing.push("Dari: Alamat Perusahaan");

    if (!formData.billToCompanyName) missing.push("Bill To: Nama Perusahaan");
    if (!formData.billToPicName) missing.push("Bill To: PIC Name");
    if (!formData.billToCompanyAddress) missing.push("Bill To: Alamat Perusahaan");

    if (!formData.paymentBank) missing.push("Pembayaran: Bank");
    if (!formData.paymentAccountName) missing.push("Pembayaran: Nama Rekening");
    if (!formData.paymentAccountNo) missing.push("Pembayaran: No. Rekening");

    const validLineCount = (formData.lines || []).filter((l) => (Number(l.qty) || 0) >= 1 && String(l.productName || "").trim()).length;
    if (!validLineCount) missing.push("Produk minimal 1 baris (nama terisi, qty minimal 1)");

    if (formData.invoiceDate && formData.dueDate) {
      const inv = new Date(formData.invoiceDate);
      const due = new Date(formData.dueDate);
      if (due < inv) missing.push("Tanggal jatuh tempo harus sama atau setelah tanggal invoice.");

    }

    const ppnPct = Number(formData.ppnPercent);
    if (Number.isNaN(ppnPct) || ppnPct < 0 || ppnPct > 100) missing.push("PPN(%) harus 0 - 100");

    if (missing.length) {
      toast.error(
  <div>
    <div className="font-semibold mb-1">Lengkapi / perbaiki field berikut:</div>
    <ul className="list-disc pl-5">
      {missing.map((m) => (
        <li key={m}>{m}</li>
      ))}
    </ul>
  </div>
);

      return false;
    }
    return true;
  };

  const submitSaveNoStatusChange = async () => {
    if (submitting) return;

    if (!hasAnyMeaningfulDraftContent()) {
      toast.warning("Minimal isi 1 field dulu sebelum menyimpan.");

      return;
    }
    const err = validateDatePair(formData.invoiceDate, formData.dueDate);
if (err) {
  toast.error(err);
  return;
}

    try {
      setSubmitting(true);
      const apiPayload = await buildApiPayload({ status: isEditMode ? undefined : "draft" });
      await onSubmit?.(apiPayload, { mode: "save" });
    } finally {
      setSubmitting(false);
    }
  };



  const submitIssued = async () => {
    if (submitting) return;
    if (!validateForIssued()) return;

    try {
      setSubmitting(true);
      const apiPayload = await buildApiPayload({ status: "issued" });
      await onSubmit?.(apiPayload, { mode: "issued" });
    } finally {
      setSubmitting(false);
    }
  };

  // ensure at least 1 row exists (works after duplicate/reset)
  useEffect(() => {
    if ((formData.lines || []).length === 0) addLine();
  }, [formData.lines?.length, addLine]);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-6xl max-h-[90vh] overflow-y-auto shadow-xl border border-gray-200">
        {/* HEADER */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{isEditMode ? "Edit Invoice Penjualan" : "Buat Invoice Penjualan"}</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {isEditMode ? "Perbarui data invoice yang sudah tersimpan." : "Isi data invoice, lalu simpan sebagai draft atau issue."}
            </p>
          </div>

          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* DATES */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tgl. Invoice <span className="text-red-500">*</span>
              </label>
              <input
              type="date"
              value={formData.invoiceDate}
              max={formData.dueDate || undefined}
              onChange={(e) => {
                const v = e.target.value;
                setFormData((prev) => ({ ...prev, invoiceDate: v }));
                setDateError(validateDatePair(v, formData.dueDate));
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required={false}
              disabled={lockSalesLinkedFields}
            />

              <p className="text-xs text-gray-500 mt-1">
                Wajib untuk <b>Issue</b>. Untuk <b>Draft</b> boleh kosong.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tgl. Jatuh Tempo <span className="text-red-500">*</span>
              </label>
              <input
                  type="date"
                  value={formData.dueDate}
                  min={formData.invoiceDate || undefined}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFormData((prev) => ({ ...prev, dueDate: v }));
                    setDateError(validateDatePair(formData.invoiceDate, v));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required={false}
                  disabled={lockSalesLinkedFields}
                />

              <p className="text-xs text-gray-500 mt-1">
                Wajib untuk <b>Issue</b>. Untuk <b>Draft</b> boleh kosong.
              </p>
            </div>
          </div>

          {/* FROM / BILL TO */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* FROM */}
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-800 mb-3">Dari (Perusahaan Anda)</h3>

              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nama Perusahaan <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.fromCompanyName}
                    onChange={(e) => setFormData((prev) => ({ ...prev, fromCompanyName: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Nama perusahaan Anda"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Perusahaan <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={formData.fromCompanyEmail}
                    onChange={(e) => setFormData((prev) => ({ ...prev, fromCompanyEmail: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="email@perusahaan.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Alamat Perusahaan <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={formData.fromCompanyAddress}
                    onChange={(e) => setFormData((prev) => ({ ...prev, fromCompanyAddress: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Alamat lengkap perusahaan Anda"
                  />
                </div>
              </div>
            </div>

            {/* BILL TO */}
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-800 mb-3">Ditagihkan Kepada (Bill To)</h3>

              <div className="space-y-3">
                {/* Autocomplete input */}
                <div ref={billToInputWrapRef} className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nama Perusahaan <span className="text-red-500">*</span>
                  </label>

                  <div className="relative">
                    <input
                      ref={billToInputRef}
                      type="text"
                      value={formData.billToCompanyName}
                      onChange={(e) => {
                        const v = e.target.value;
                        setFormData((prev) => ({ ...prev, billToCompanyName: v }));
                        setBillToSuggestOpen(Boolean(String(v || "").trim()));
                        setBillToActiveIdx(0);
                      }}
                      onFocus={() => {
                        if (String(formData.billToCompanyName || "").trim()) setBillToSuggestOpen(true);
                      }}
                      onKeyDown={(e) => {
                        if (!billToSuggestOpen) {
                          if (e.key === "ArrowDown" && filteredBillToSuggestions.length) {
                            setBillToSuggestOpen(true);
                            setBillToActiveIdx(0);
                            e.preventDefault();
                          }
                          return;
                        }

                        if (e.key === "Escape") {
                          setBillToSuggestOpen(false);
                          setBillToActiveIdx(-1);
                          e.preventDefault();
                          return;
                        }

                        if (e.key === "ArrowDown") {
                          e.preventDefault();
                          setBillToActiveIdx((idx) => {
                            const next = Math.min(filteredBillToSuggestions.length - 1, (idx < 0 ? 0 : idx) + 1);
                            return next;
                          });
                          return;
                        }

                        if (e.key === "ArrowUp") {
                          e.preventDefault();
                          setBillToActiveIdx((idx) => {
                            const next = Math.max(0, (idx < 0 ? 0 : idx) - 1);
                            return next;
                          });
                          return;
                        }

                        if (e.key === "Enter") {
                          if (filteredBillToSuggestions.length && billToActiveIdx >= 0) {
                            e.preventDefault();
                            selectBillToSuggestion(filteredBillToSuggestions[billToActiveIdx]);
                          }
                          return;
                        }
                      }}
                      className="w-full px-3 py-2 pr-9 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder={loadingClients ? "Memuat client..." : "Ketik nama mitra..."}
                      aria-expanded={billToSuggestOpen}
                      aria-controls="billto-suggestions"
                      aria-autocomplete="list"
                      disabled = {lockSalesLinkedFields}
                    />
                    <Search className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2" />
                  </div>

                  {billToSuggestOpen && filteredBillToSuggestions.length > 0 && (
                    <div
                      id="billto-suggestions"
                      role="listbox"
                      className="absolute z-20 mt-2 w-full rounded-xl border border-gray-200 bg-white shadow-lg overflow-hidden"
                    >
                      {filteredBillToSuggestions.map((c, idx) => {
                        const name = c?.client_name || c?.companyName || c?.name || "-";
                        const code = c?.client_id ? `(${c.client_id})` : "";
                        const addr = c?.client_address || c?.address || "";
                        const pic = c?.pic_name || c?.picName || c?.pic || "";
                        const emailOrPhone = c?.email_or_phone || c?.emailOrPhone || c?.email || c?.phone || "";

                        const active = idx === billToActiveIdx;
                        return (
                          <button
                            key={`${c?._id || name}_${idx}`}
                            type="button"
                            role="option"
                            aria-selected={active}
                            onMouseEnter={() => setBillToActiveIdx(idx)}
                            onMouseDown={(e) => {
                              // prevent input blur before click
                              e.preventDefault();
                            }}
                            onClick={() => selectBillToSuggestion(c)}
                            className={[
                              "w-full text-left px-4 py-3 transition-colors",
                              active ? "bg-blue-50" : "bg-white hover:bg-gray-50",
                            ].join(" ")}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div className="font-semibold text-gray-900 truncate">
                                {name} <span className="text-gray-500 font-medium">{code}</span>
                              </div>
                            </div>
                            <div className="mt-1 text-xs text-gray-600 line-clamp-2">
                              {pic ? `PIC: ${pic}` : null}
                              {pic && emailOrPhone ? " • " : null}
                              {emailOrPhone ? `${emailOrPhone}` : null}
                              {(pic || emailOrPhone) && addr ? " • " : null}
                              {addr ? `${addr}` : null}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {billToSuggestOpen && String(formData.billToCompanyName || "").trim() && filteredBillToSuggestions.length === 0 && (
                    <div className="absolute z-20 mt-2 w-full rounded-xl border border-gray-200 bg-white shadow-lg px-4 py-3 text-sm text-gray-600">
                      Tidak ada suggestion.
                    </div>
                  )}

                  <p className="text-xs text-gray-500 mt-2">
                    Tips: pakai <b>↑</b>/<b>↓</b> lalu <b>Enter</b> untuk pilih.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      PIC Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.billToPicName}
                      onChange={(e) => setFormData((prev) => ({ ...prev, billToPicName: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Nama PIC"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">PIC Title</label>
                    <input
                      type="text"
                      value={formData.billToPicTitle}
                      onChange={(e) => setFormData((prev) => ({ ...prev, billToPicTitle: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Jabatan PIC"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Alamat Perusahaan <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={formData.billToCompanyAddress}
                    onChange={(e) => setFormData((prev) => ({ ...prev, billToCompanyAddress: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Alamat lengkap perusahaan mitra"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email / Phone</label>
                    <input
                      type="text"
                      value={formData.billToEmailOrPhone}
                      onChange={(e) => setFormData((prev) => ({ ...prev, billToEmailOrPhone: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Email atau nomor telepon"
                    />
                  </div>

                {showReferenceField && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Reference</label>
                    <input
                      type="text"
                      value={formData.billToReference}
                      onChange={(e) => setFormData((prev) => ({ ...prev, billToReference: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Referensi (Sales ID)"
                      disabled={lockSalesLinkedFields}
                    />
                  </div>
                )}

                </div>
              </div>
            </div>
          </div>

          {/* PAYMENT */}
          <div className="border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-800 mb-3">Informasi Pembayaran</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bank <span className="text-red-500">*</span>
                </label>

                <input
                  type="text"
                  list="bankSuggestions"
                  value={formData.paymentBank}
                  onChange={(e) => setFormData((prev) => ({ ...prev, paymentBank: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ketik atau pilih bank"
                />
                <datalist id="bankSuggestions">
                  {BANK_SUGGESTIONS.map((b) => (
                    <option key={b} value={b} />
                  ))}
                </datalist>

                <p className="text-xs text-gray-500 mt-1">Bisa ketik manual kalau bank tidak ada di list.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nama Rekening <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.paymentAccountName}
                  onChange={(e) => setFormData((prev) => ({ ...prev, paymentAccountName: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Atas nama"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  No. Rekening <span className="text-red-500">*</span>
                </label>
               <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={formData.paymentAccountNo}
                onChange={(e) => {
                  const digitsOnly = e.target.value.replace(/\D/g, "");
                  setFormData((prev) => ({ ...prev, paymentAccountNo: digitsOnly }));
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Nomor rekening"
                autoComplete="off"
              />

              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Catatan Pembayaran</label>
                <input
                  type="text"
                  value={formData.paymentNotes}
                  onChange={(e) => setFormData((prev) => ({ ...prev, paymentNotes: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Contoh: Transfer sebelum jatuh tempo"
                />
              </div>
            </div>
          </div>

          {/* ITEMS (match Sales picture: Name/Description/Qty/Price/Amount) */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Items</h3>
                <p className="text-xs text-gray-500">Amount otomatis = qty × price (mengikuti schema Sales)</p>
              </div>

              <button
                type="button"
                onClick={addLine}
                disabled={lockSalesLinkedFields || submitting}

                className="inline-flex items-center gap-2 px-3 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              >
                <Plus className="w-4 h-4" />
                Tambah Item
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-12">#</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider min-w-[220px]">
                      Product
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider min-w-[320px]">
                      Description
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider w-28">Qty</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider w-44">Price</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider w-44">Amount</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider w-20">Aksi</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200">
                  {(formData.lines || []).map((line, idx) => (
                    <tr key={line.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-600">{idx + 1}</td>

                      <td className="px-4 py-3">
                        <input
                          type="text"
                          value={line.productName}
                          onChange={(e) => updateLine(line.id, "productName", e.target.value)}
                          placeholder="Nama item"
                          className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                         disabled={submitting || lockSalesLinkedFields}

                        />
                      </td>

                      <td className="px-4 py-3 align-middle">
                        <textarea
                          value={line.description}
                          onChange={(e) => updateLine(line.id, "description", e.target.value)}
                          onInput={(e) => autoGrowTextarea(e.currentTarget)}
                          onFocus={(e) => autoGrowTextarea(e.currentTarget)}
                          placeholder="Deskripsi item"
                          rows={1}
                          className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y min-h-[42px] overflow-hidden"
                        disabled={submitting || lockSalesLinkedFields}

                        />
                      </td>

                      <td className="px-4 py-3 text-center">
                      <input
  type="number"
  min="1"
  step="1"
  value={line.qty}
  onKeyDown={(e) => {
    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      e.preventDefault();
    }
  }}
  onChange={(e) => updateLine(line.id, "qty", e.target.value)}

                          className="no-spinner w-24 px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-center"
                          disabled={submitting || lockSalesLinkedFields}

                        />
                      </td>

                      <td className="px-4 py-3 text-center">
                      <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={line.unitPrice}
                      onKeyDown={(e) => {
                        if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                          e.preventDefault();
                        }
                      }}
                      onChange={(e) => updateLine(line.id, "unitPrice", e.target.value)}

                          className="no-spinner w-40 px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-center"
                        disabled={submitting || lockSalesLinkedFields}

                        />
                      </td>

                      <td className="px-4 py-3 text-right font-semibold text-gray-900 whitespace-nowrap">
                        {formatCurrency(line.lineTotal)}
                      </td>

                      <td className="px-4 py-3 text-center">
                        <button
                          type="button"
                          onClick={() => removeLine(line.id)}
                          className="inline-flex items-center justify-center w-9 h-9 rounded-xl text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
                          disabled={lockSalesLinkedFields || (formData.lines || []).length <= 1 || submitting}

                          title="Hapus item"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>

                <tfoot>
  <tr className="bg-white border-t border-gray-200">
    {/* label cell spans all columns except the Amount column */}
    <td colSpan={5} className="px-4 py-3 text-right text-sm font-semibold text-gray-700">
      Subtotal
    </td>

    {/* amount cell (aligned like sales) */}
    <td className="px-4 py-3 text-right font-semibold text-gray-900 whitespace-nowrap">
      {formatCurrency(calculateSubtotal())}
    </td>

    {/* action column empty */}
    <td className="px-4 py-3" />
  </tr>
</tfoot>

              </table>
            </div>
          </div>

        {/* TOTALS (match CreateSalesForm look) */}
<div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
  {/* LEFT: PPN/PPH/Discount compact rows */}
  <div className="border rounded-2xl border-gray-200 p-4 bg-white">
    <h3 className="text-sm font-semibold text-gray-800 mb-3">Persentase (Optional)</h3>

    <div className="space-y-3">
      <div className="flex items-center justify-between gap-4">
        <label className="text-sm font-semibold text-gray-700 whitespace-nowrap">
          PPN (%) <span className="text-gray-400 font-normal">(default 11)</span>
        </label>

<input
  type="number"
  min="0"
  max="100"
  step="0.01"
  value={formData.ppnPercent}
  onKeyDown={(e) => {
    if (e.key === "ArrowUp" || e.key === "ArrowDown") e.preventDefault();
  }}
  onChange={(e) => setFormData((p) => ({ ...p, ppnPercent: e.target.value }))}
  className="no-spinner w-44 px-3 py-2 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
  disabled={submitting || lockSalesLinkedFields}
/>


      </div>

      <div className="flex items-center justify-between gap-4">
        <label className="text-sm font-semibold text-gray-700 whitespace-nowrap">PPh (%)</label>

<input
  type="number"
  min="0"
  max="100"
  step="0.01"
  value={formData.pphPercent}
  onKeyDown={(e) => {
    if (e.key === "ArrowUp" || e.key === "ArrowDown") e.preventDefault();
  }}
  onChange={(e) => setFormData((p) => ({ ...p, pphPercent: e.target.value }))}
  className="no-spinner w-44 px-3 py-2 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
  disabled={submitting || lockSalesLinkedFields}
/>


      </div>

      <div className="flex items-center justify-between gap-4">
        <label className="text-sm font-semibold text-gray-700 whitespace-nowrap">Discount (%)</label>

        <input
          type="number"
          min="0"
          max="100"
          step="0.01"
          value={formData.discountPercent}
          onKeyDown={(e) => {
            if (e.key === "ArrowUp" || e.key === "ArrowDown") e.preventDefault();
          }}
          onChange={(e) => setFormData((p) => ({ ...p, discountPercent: e.target.value }))}
          className="no-spinner w-44 px-3 py-2 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
          disabled={submitting || lockSalesLinkedFields}
        />

      </div>

      <p className="text-xs text-gray-500">
        PPh mengurangi Grand Total (Base + PPN - PPh).
      </p>
    </div>
  </div>

  {/* RIGHT: Totals with Rp + value aligned like CreateSalesForm */}
  <div className="lg:col-span-2 border rounded-2xl border-gray-200 p-4 bg-gray-50">
    <h3 className="text-sm font-semibold text-gray-800 mb-3">Totals</h3>

    <div className="space-y-3 text-sm">
 


      {/* Total PPN */}
      <div className="flex items-center justify-between">
        <span className="text-gray-600">Total PPN</span>
        <span className="inline-flex items-center gap-3 font-semibold text-gray-900">
          <span className="w-8 text-left">Rp</span>
          <span className="w-28 text-right">
            {new Intl.NumberFormat("id-ID").format(Number(calculatePpnAmount() || 0))}
          </span>
        </span>
      </div>

      {/* Total PPh */}
      <div className="flex items-center justify-between">
        <span className="text-gray-600">Total PPh</span>
        <span className="inline-flex items-center gap-3 font-semibold text-gray-900">
          <span className="w-8 text-left">Rp</span>
          <span className="w-28 text-right">
            {new Intl.NumberFormat("id-ID").format(Number(calculatePphAmount() || 0))}
          </span>
        </span>
      </div>
                        {/* Total Discount */}
      <div className="flex items-center justify-between">
        <span className="text-gray-600">Total Discount</span>
        <span className="inline-flex items-center gap-3 font-semibold text-gray-900">
          <span className="w-8 text-left">Rp</span>
          <span className="w-28 text-right">
            {new Intl.NumberFormat("id-ID").format(Number(calculateDiscountAmount() || 0))}
          </span>
        </span>
      </div>

      {/* Grand Total */}
      <div className="border-t border-gray-200 pt-3 flex items-center justify-between">
        <span className="text-base font-bold text-gray-900">Grand Total</span>
        <span className="text-base font-bold text-blue-600">
          {formatCurrency(calculateGrandTotal())}
        </span>
      </div>


      
    </div>
  </div>
</div>


          {/* SIGNER */}
          <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-gray-800">Data Penandatangan</p>
                <p className="text-xs text-gray-500">Isi tanggal, nama, dan jabatan. Tidak perlu upload tanda tangan/meterai.</p>
              </div>
            </div>

            <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tanggal Penandatangan</label>
                <input
                  type="date"
                  value={formData.signatureDate}
                  onChange={(e) => setFormData((prev) => ({ ...prev, signatureDate: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nama Penandatangan</label>
                <input
                  type="text"
                  value={formData.signatureName}
                  onChange={(e) => setFormData((prev) => ({ ...prev, signatureName: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  placeholder="Nama"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Jabatan Penandatangan</label>
                <input
                  type="text"
                  value={formData.signatureTitle}
                  onChange={(e) => setFormData((prev) => ({ ...prev, signatureTitle: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  placeholder="Jabatan"
                />
              </div>
            </div>
          </div>

          {/* ACTIONS */}
          <div className="flex flex-col sm:flex-row gap-3 justify-end pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              disabled={submitting}
            >
              Batalkan
            </button>

            {/* Save without changing status */}
            <button
              type="button"
              onClick={submitSaveNoStatusChange}
              className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors inline-flex items-center justify-center gap-2"
              disabled={submitting}
              title={isEditMode ? "Simpan perubahan tanpa mengubah status" : "Simpan (buat draft)"}
            >
              <CheckCircle2 className="w-4 h-4" />
              {submitting ? "Menyimpan..." : (isEditMode ? "Simpan" : "Simpan Draft")}

            </button>

         
            {/* Issue */}
       {canIssue && (
  <button
    type="button"
    onClick={submitIssued}
    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center justify-center gap-2"
    disabled={submitting}
    title="Issue invoice (wajib lengkap)"
  >
    <FileText className="w-4 h-4" />
    {submitting ? "Menyimpan..." : "Issue Invoice"}
  </button>
)}

          </div>
        </div>
      </div>
    </div>
  );
}
